import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { RateGrid } from "@/components/rates/RateGrid";
import { BulkEditPanel } from "@/components/rates/BulkEditPanel";
import { RatePlanEditor } from "@/components/rates/RatePlanEditor";
import { QuoteSimulator } from "@/components/rates/QuoteSimulator";
import { DiscountCodesCard } from "@/components/rates/DiscountCodesCard";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (s, n) => { const d = new Date(s + "T00:00:00"); d.setDate(d.getDate() + n); return iso(d); };
const fmt = (s) => s.split("-").reverse().join(".");

export default function RatesAvailability() {
  const [units, setUnits] = useState([]);
  const [slug, setSlug] = useState("");
  const [from, setFrom] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [selStart, setSelStart] = useState(null);
  const [selEnd, setSelEnd] = useState(null);
  const to = addDays(from, 30);

  useEffect(() => {
    api.get("/rates/units").then((r) => { setUnits(r.data); if (r.data[0]) setSlug(r.data[0].slug); })
      .catch(() => toast.error("Einheiten konnten nicht geladen werden"));
  }, []);

  const load = useCallback(() => {
    if (!slug) return;
    api.get(`/rates/${slug}/calendar`, { params: { start: from, end: to } }).then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Kalender konnte nicht geladen werden"));
  }, [slug, from, to]);
  useEffect(() => { load(); }, [load]);

  const onDayClick = (d) => {
    if (!selStart || selEnd) { setSelStart(d); setSelEnd(null); return; }
    if (d < selStart) { setSelEnd(selStart); setSelStart(d); } else setSelEnd(d);
  };

  const onPrice = async (date, change) => {
    try {
      await api.post(`/rates/${slug}/days`, { start: date, end: date, ...change });
      toast.success(`Preis für ${fmt(date)} gespeichert`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };

  return (
    <div className="space-y-6" data-testid="rates-page">
      <div>
        <h1 className="text-3xl font-semibold" style={{ fontFamily: "'Work Sans',sans-serif" }}>Raten & Verfügbarkeit</h1>
        <p className="text-sm text-muted-foreground mt-1">Preise, Mindestaufenthalt und Sperren je Unterkunft. Änderungen gelten nach dem Speichern sofort auf der Homepage und werden als belegt an Booking.com/Airbnb gemeldet.</p>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="rates-unit-tabs">
        {units.map((u) => (
          <button key={u.slug} onClick={() => { setSlug(u.slug); setSelStart(null); setSelEnd(null); }} data-testid={`rates-unit-${u.slug}`}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${slug === u.slug ? "bg-primary text-white border-primary" : "border-border hover:bg-muted"}`}>{u.name}</button>
        ))}
        <span className="px-4 py-2 rounded-full text-sm border border-dashed border-border text-muted-foreground" data-testid="rates-unit-fewo">Ferienwohnung komplett · nur auf Anfrage</span>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => setFrom(addDays(from, -30))} data-testid="rates-prev"><CaretLeft size={16}/></Button>
        <input type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} className="h-9 border border-border rounded-md px-2 text-sm bg-background" data-testid="rates-from"/>
        <span className="text-sm text-muted-foreground">bis {fmt(to)}</span>
        <Button variant="outline" size="icon" onClick={() => setFrom(addDays(from, 30))} data-testid="rates-next"><CaretRight size={16}/></Button>
        {selStart && <span className="ml-auto text-sm" data-testid="rates-selection">Ausgewählt: {fmt(selEnd && selEnd < selStart ? selEnd : selStart)} – {fmt(selEnd && selEnd > selStart ? selEnd : selStart)}</span>}
      </div>
      {data && <RateGrid days={data.days} selStart={selStart} selEnd={selEnd} onDayClick={onDayClick} onPrice={onPrice}/>}
      <div className="flex gap-4 text-xs text-muted-foreground">
        <span><b className="text-primary">Fett</b> = eigener Tageswert · Preis anklicken zum Ändern</span><span className="text-emerald-700">Frei</span><span className="text-red-700">Gesperrt</span><span className="text-amber-700">Belegt (Buchung)</span>
      </div>
      <div className="grid lg:grid-cols-[360px_1fr] gap-6 items-start">
        <div className="space-y-6">
          <BulkEditPanel slug={slug} start={selStart} end={selEnd || selStart} setStart={setSelStart} setEnd={setSelEnd} onSaved={load}/>
          <QuoteSimulator slug={slug} maxGuests={data?.plan?.max_guests}/>
        </div>
        <div className="space-y-6">
          <RatePlanEditor slug={slug} plan={data?.plan} onSaved={load}/>
          <DiscountCodesCard/>
        </div>
      </div>
    </div>
  );
}
